import React from "react";
import { LoginPage } from "./auth/LoginPage";
import DashboardPage from "./dashboard/DashboardPage";
import { SessionProvider } from "./providers/SessionProvider";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AppRoutes } from "./AppRoutes";
import { AuthorizedPage } from "./root/AuthorizedPage";
import { CreativesListPage } from "./creatives/list";
import { CreativeViewPage } from "./creatives/view";
import { VastBuilderPage } from "./creatives/vast-builder-page";
import { UserPage } from "./auth/UserPage";
import { MappingListPage } from "./wizard/mapping/MappingListPage";
import { MappingViewPage } from "./wizard/mapping/MappingViewPage";
import { AdapterViewPage } from "./wizard/release/adapter/AdapterViewPage";
import { AdapterListPage } from "./wizard/release/adapter/AdapterListPage";
import { SdkListPage } from "./wizard/release/sdk/SdkListPage";
import { SdkViewPage } from "./wizard/release/sdk/SdkViewPage";
import { useLocalStorage } from "@rehooks/local-storage";
import { ConfigProvider, FloatButton, theme } from "antd";
import {
  BuildOutlined,
  MoonOutlined,
  SunOutlined,
  CompressOutlined,
  ExpandOutlined,
} from "@ant-design/icons";
import { SpmPage } from "./wizard/spm/SpmPage";
import { CrawlerPage } from "./wizard/crawler/CrawlerPage";
import { SystemHealthPage } from "./wizard/system-health/SystemHealthPage";
import { SKAdNetworkListPage } from "./wizard/skadn/SKAdNetworkListPage";
import { AdAttributionKitListPage } from "./wizard/aak/AdAttributionKitListPage";
import { RecommendationsPage } from "./wizard/recommendations/RecommendationsPage";

import "./App.scss";
import { PluginListPage } from "./wizard/release/plugin/PluginListPage";
import { PluginViewPage } from "./wizard/release/plugin/PluginViewPage";
import { AdaptiveRenderingPage } from "./creatives/adaptive-rendering/AdaptiveRenderingPage";
import { CreativeValidatorPage } from "./creatives/CreativeValidatorPage";

const AppRouter = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path={AppRoutes.Login} element={<LoginPage />} />
        <Route element={<AuthorizedPage />}>
          <Route path="/" element={<Navigate to={AppRoutes.Dashboard} />} />
          <Route path={AppRoutes.Dashboard} element={<DashboardPage />} />

          <Route path={AppRoutes.Creatives} element={<CreativesListPage />} />
          <Route path={AppRoutes.CreativeInfo} element={<CreativeViewPage />} />
          <Route path={AppRoutes.VastBuilder} element={<VastBuilderPage />} />
          <Route
            path={AppRoutes.AdaptiveRendering}
            element={<AdaptiveRenderingPage />}
          />
          <Route
            path={AppRoutes.CreativeValidator}
            element={<CreativeValidatorPage />}
          />
          <Route path="/user" element={<UserPage />} />

          <Route
            path={AppRoutes.Wizard.MappingList}
            element={<MappingListPage />}
          />
          <Route
            path={AppRoutes.Wizard.Mapping}
            element={<MappingViewPage />}
          />
          <Route
            path={AppRoutes.Wizard.AdapterList}
            element={<AdapterListPage />}
          />
          <Route
            path={AppRoutes.Wizard.Adapter}
            element={<AdapterViewPage />}
          />
          <Route path={AppRoutes.Wizard.SdkList} element={<SdkListPage />} />
          <Route path={AppRoutes.Wizard.Sdk} element={<SdkViewPage />} />

          <Route
            path={AppRoutes.Wizard.PluginList}
            element={<PluginListPage />}
          />
          <Route path={AppRoutes.Wizard.Plugin} element={<PluginViewPage />} />

          <Route path={AppRoutes.Wizard.Spm} element={<SpmPage />} />
          <Route path={AppRoutes.Wizard.Crawler} element={<CrawlerPage />} />
          <Route
            path={AppRoutes.Wizard.SystemHealth}
            element={<SystemHealthPage />}
          />
          <Route
            path={AppRoutes.Wizard.SKAdNetwork}
            element={<SKAdNetworkListPage />}
          />
          <Route
            path={AppRoutes.Wizard.AdAttributionKit}
            element={<AdAttributionKitListPage />}
          />
          <Route
            path={AppRoutes.Wizard.Recommendations}
            element={<RecommendationsPage />}
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

const App = () => {
  const [isDarkTheme, setDarkTheme] = useLocalStorage(
    "dark_theme",
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
  const [isCompact, setCompact] = useLocalStorage("compact_theme", false);

  const algorithms = () => {
    var result: any[] = [];

    if (isDarkTheme) {
      result.push(theme.darkAlgorithm);
    } else {
      result.push(theme.defaultAlgorithm);
    }
    if (isCompact) {
      result.push(theme.compactAlgorithm);
    }

    return result;
  };

  return (
    <ConfigProvider
      theme={{
        algorithm: algorithms(),
        components: {
          Typography: {
            colorText: isDarkTheme ? "#fff" : "#000",
          },
          Layout: {
            headerPadding: "0 1rem",
            algorithm: true,
            headerBg: isDarkTheme
              ? "linear-gradient(135deg,rgba(6,147,227,1) 0%,rgb(155,81,224) 100%)"
              : "linear-gradient(135deg,rgb(255,245,203) 0%,rgb(182,227,212) 50%,rgb(51,167,181) 100%)",
            footerBg: isDarkTheme
              ? "linear-gradient(135deg,rgb(155,81,224) 0%,rgba(6,147,227,1) 100%)"
              : "linear-gradient(135deg,rgb(51,167,181) 0%,rgb(182,227,212) 50%,rgb(255,245,203) 100%)",
          },
        },
      }}
    >
      <SessionProvider>
        <AppRouter />
      </SessionProvider>
      <FloatButton.Group
        trigger="click"
        style={{ right: 24 }}
        icon={<BuildOutlined  />}
      >
        <FloatButton
          onClick={() => setDarkTheme(!isDarkTheme)}
          icon={
            isDarkTheme ? (
              <SunOutlined  />
            ) : (
              <MoonOutlined  />
            )
          }
        />
        <FloatButton
          onClick={() => setCompact(!isCompact)}
          icon={
            isCompact ? (
              <ExpandOutlined  />
            ) : (
              <CompressOutlined  />
            )
          }
        />
      </FloatButton.Group>
    </ConfigProvider>
  );
};

export default App;
